import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { MapPin, IndianRupee, FilePlus2, RotateCcw, Download, Layers, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Spinner, EmptyState } from "../../components/common";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { api, errMsg, fmtINR, fmtDate, downloadBlob } from "../../lib/api";
import { useLiveCollections } from "../../lib/liveCollections";
import { useRoleModules } from "../../lib/roleModules";
import { FetchButton, FetchPrompt, useFetchGate } from "../../components/FetchBar";
import { FieldActivityDetailDialog, spanDays } from "../../components/FieldActivityTable";
import { HeadWiseReport } from "../../components/HeadWiseReport";   // Phase 230
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";

const isoDay = (d) => {
  const z = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return z.toISOString().slice(0, 10);
};

const PERIODS = [
  { key: "today", label: "Today", days: 0 },
  { key: "7d", label: "7 days", days: 6 },
  { key: "30d", label: "30 days", days: 29 },
  { key: "custom", label: "Custom", days: null },
];

const KPIS = [
  { key: "visits", metric: "visits", label: "Visits", icon: MapPin },
  { key: "collections", metric: "collections", label: "Collected", icon: IndianRupee },
  { key: "new_surveys", metric: "surveys", label: "Surveys", icon: FilePlus2 },
  { key: "reassessments", metric: "reassessments", label: "Reassess", icon: RotateCcw },
];

const DAY_STATS = [
  { key: "visits", metric: "visits", label: "Visits" },
  { key: "collections_count", metric: "collections", label: "Receipts" },
  { key: "new_surveys", metric: "surveys", label: "Surveys" },
  { key: "reassessments", metric: "reassessments", label: "Reassess" },
];

export default function CollectorReports() {
  const { code } = useParams();
  // Phase 230: "By tax head" (demand / collected / outstanding per head for my territory) beside the day-wise report;
  // module `reports` "Full access" = Excel / PDF, "View only" = on screen
  const [tab, setTab] = useState("days");
  const access = useRoleModules(code);
  const canDownloadHeadWise = !access.loaded || access.levelOf("reports") === "write";
  const [period, setPeriod] = useState("7d");
  const [fromDate, setFromDate] = useState(isoDay(new Date()));
  const [toDate, setToDate] = useState(isoDay(new Date()));
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);
  const [exporting, setExporting] = useState(false);

  const rangeFor = (per, from, to) => {
    const now = new Date();
    const p = PERIODS.find((x) => x.key === per);
    if (per === "custom") return { date_from: from, date_to: to };
    const start = new Date(now);
    start.setDate(start.getDate() - (p?.days || 0));
    return { date_from: isoDay(start), date_to: isoDay(now) };
  };
  // Phase 133: the report runs only on Fetch, for the period applied at that moment
  const gate = useFetchGate({ period, fromDate, toDate });
  const [loading, setLoading] = useState(false);
  const a = gate.applied;
  const range = useCallback(() => (a ? rangeFor(a.period, a.fromDate, a.toDate) : rangeFor(period, fromDate, toDate)),
    [gate.appliedKey, period, fromDate, toDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const live = useLiveCollections(code);   // Phase 149: collection figures follow the live ledger
  useEffect(() => {
    if (!gate.fetched) return;
    setLoading(true);
    api.get(`/t/${code}/reports/field-activity`, { params: rangeFor(a.period, a.fromDate, a.toDate) })
      .then((r) => setData(r.data)).catch(() => setData({ rows: [], totals: {} }))
      .finally(() => setLoading(false));
  }, [code, gate.appliedKey, gate.fetched, gate.version, live]); // eslint-disable-line react-hooks/exhaustive-deps

  const t = data?.totals || {};
  const activeDays = (data?.rows || []).filter((r) =>
    r.visits || r.collections_count || r.new_surveys || r.reassessments);

  const rng = range();
  const openDetail = (metric, day) =>
    setDetail({ metric, dateFrom: day || rng.date_from, dateTo: day || rng.date_to });

  // Phase 232: any custom range is allowed (no 92-day cap) — only "start after end" is refused
  const customInvalid = period === "custom" && !!fromDate && !!toDate && fromDate > toDate;
  const appliedDays = spanDays(rng.date_from, rng.date_to);

  const doExport = async () => {
    setExporting(true);
    const tid = toast.loading(`Preparing the Excel file for ${appliedDays} day${appliedDays === 1 ? "" : "s"}${appliedDays > 92 ? " — a long range can take a little while" : ""}…`);
    try {
      await downloadBlob(`/t/${code}/reports/field-activity/export?date_from=${rng.date_from}&date_to=${rng.date_to}`,
        `my-field-report_${rng.date_from}_to_${rng.date_to}.xlsx`);
      toast.success("Excel report downloaded", { id: tid });
    } catch (e) { toast.error(errMsg(e), { id: tid }); } finally { setExporting(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h1 className="text-lg font-semibold font-heading">My reports</h1>
          <p className="text-sm text-muted-foreground">
            {tab === "heads"
              ? "Demand, collected and outstanding per tax head in your territory — tap a head for its breakdowns"
              : "Day-wise visits, collections, surveys and reassessments — tap any number for details"}
          </p>
        </div>
        {tab === "days" && (
          <Button size="sm" className="h-9 shrink-0" onClick={doExport} disabled={exporting || !data || !gate.fetched}
            data-testid="reports-export-button">
            <Download className="h-4 w-4 mr-1.5" /> {exporting ? "..." : "Excel"}
          </Button>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab} data-testid="reports-tabs">
        <TabsList className="grid w-full grid-cols-2 h-10">
          <TabsTrigger value="days" className="gap-1.5" data-testid="reports-tab-days"><MapPin className="h-3.5 w-3.5" /> Day-wise</TabsTrigger>
          <TabsTrigger value="heads" className="gap-1.5" data-testid="reports-tab-heads"><Layers className="h-3.5 w-3.5" /> By tax head</TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === "heads" ? (
        <HeadWiseReport code={code} testIdPrefix="collector-head-wise" showArea={false} compact hideHeader canDownload={canDownloadHeadWise} />
      ) : (
      <>
      <div className="flex flex-wrap gap-1.5" data-testid="reports-period-chips">
        {PERIODS.map((p) => (
          <button key={p.key} type="button" onClick={() => setPeriod(p.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              period === p.key ? "bg-primary text-primary-foreground border-primary" : "bg-card text-muted-foreground hover:bg-muted"
            }`}
            data-testid={`reports-period-${p.key}`}>
            {p.label}
          </button>
        ))}
      </div>
      {period === "custom" && (
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label className="text-xs">From</Label>
            <Input type="date" className="h-10" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
              data-testid="reports-from-date" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">To</Label>
            <Input type="date" className="h-10" value={toDate} onChange={(e) => setToDate(e.target.value)}
              data-testid="reports-to-date" />
          </div>
        </div>
      )}
      <FetchButton onClick={gate.fetch} loading={loading} dirty={gate.dirty} fetched={gate.fetched} className="w-full h-11"
        disabled={period === "custom" && (!fromDate || !toDate || customInvalid)} testId="reports-fetch-button" />
      {customInvalid && (
        <p className="text-xs text-destructive" role="alert" data-testid="reports-range-error">Start date must not be after the end date.</p>
      )}
      {loading && (
        <p className="text-xs text-muted-foreground flex items-center gap-1.5" role="status" aria-live="polite" data-testid="reports-loading-note">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading {appliedDays} day{appliedDays === 1 ? "" : "s"}{appliedDays > 92 ? " — a long range can take a little while, please wait" : ""}…
        </p>
      )}

      {!gate.fetched ? (
        <Card className="shadow-sm"><CardContent className="p-0">
          <FetchPrompt onFetch={gate.fetch} testId="reports-fetch-prompt" compact title="Pick a period and tap Fetch"
            subtitle="Your day-wise visits, collections, surveys and reassessments load only when you ask for them." />
        </CardContent></Card>
      ) : !data ? <Spinner /> : (
        <>
          <div className="grid grid-cols-2 gap-3" data-testid="reports-kpis">
            {KPIS.map((k) => (
              <Card key={k.key} className="shadow-sm cursor-pointer active:scale-[0.99] transition-transform hover:border-primary/50"
                onClick={() => openDetail(k.metric)} data-testid={`reports-kpi-card-${k.key}`}>
                <CardContent className="p-3">
                  <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                    <k.icon className="h-3.5 w-3.5" /> {k.label}
                  </p>
                  {k.key === "collections" ? (
                    <p className="text-lg font-semibold tabular-nums mt-0.5" data-testid="reports-kpi-collections">
                      {fmtINR(t.collections_amount || 0)}
                      <span className="block text-[11px] font-normal text-muted-foreground">{t.collections_count || 0} receipt{(t.collections_count || 0) === 1 ? "" : "s"}</span>
                    </p>
                  ) : (
                    <p className="text-lg font-semibold tabular-nums mt-0.5" data-testid={`reports-kpi-${k.key}`}>{t[k.key] || 0}</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {activeDays.length === 0 ? (
            <EmptyState title="No field activity in this period" subtitle="Visits, collections, surveys and reassessments will appear here day by day." />
          ) : (
            <div className="space-y-3" data-testid="reports-day-list">
              {activeDays.map((r) => (
                <Card key={r.date} className="shadow-sm" data-testid={`reports-day-${r.date}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold">{fmtDate(r.date)}</p>
                      <p className="text-sm font-semibold tabular-nums">{fmtINR(r.collections_amount)}</p>
                    </div>
                    <div className="grid grid-cols-4 gap-2 mt-3 text-center">
                      {DAY_STATS.map((s) => (
                        <button key={s.key} type="button"
                          className="rounded-lg bg-muted/60 py-2 hover:bg-muted active:scale-[0.97] transition-transform"
                          onClick={() => openDetail(s.metric, r.date)}
                          data-testid={`reports-day-${r.date}-${s.key}`}>
                          <p className="text-base font-semibold tabular-nums">{r[s.key]}</p>
                          <p className="text-[10px] text-muted-foreground">{s.label}</p>
                        </button>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      )}
      </>
      )}

      <FieldActivityDetailDialog code={code} target={detail} onOpenChange={setDetail} />
    </div>
  );
}
